
import {
  LayoutDashboard,
  Users,
  ClipboardList,
  Calendar,
  Settings,
  UserCircle,
  Dumbbell,
  Apple,
  HeartPulse,
  Crown,
  TrendingUp,
  BookOpen,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/AuthProvider";

export const getDashboardNavItems = (language: 'en' | 'pt') => {
  const prefix = language === 'pt' ? '/pt' : '';
  
  return [
    {
      title: language === 'pt' ? "Visão Geral" : "Overview",
      icon: LayoutDashboard,
      href: `${prefix}/dashboard`,
    },
    {
      title: language === 'pt' ? "Agenda" : "Schedule",
      icon: Calendar,
      href: `${prefix}/dashboard/schedule`,
    },
    {
      title: language === 'pt' ? "Alunos" : "Students",
      icon: Users,
      href: `${prefix}/dashboard/students`,
    },
    {
      title: language === 'pt' ? "Treinos" : "Workouts",
      icon: Dumbbell,
      href: `${prefix}/dashboard/workouts`,
    },
    {
      title: language === 'pt' ? "Sessões" : "Sessions",
      icon: ClipboardList,
      href: `${prefix}/dashboard/training-sessions`,
    }
  ];
};

export const getStudentNavItems = (language: 'en' | 'pt', hasScheduleAccess = true) => {
  const prefix = language === 'pt' ? '/pt' : '';
  
  const baseItems = [
    {
      title: language === 'pt' ? "Visão Geral" : "Overview",
      icon: LayoutDashboard,
      href: `${prefix}/student-dashboard`,
    }
  ];
  
  const scheduleItem = hasScheduleAccess ? [
    {
      title: language === 'pt' ? "Agenda" : "Schedule",
      icon: Calendar,
      href: `${prefix}/student-dashboard/schedule`,
    }
  ] : [];
  
  const restItems = [
    {
      title: language === 'pt' ? "Treinos" : "Workouts",
      icon: Dumbbell,
      href: `${prefix}/student-dashboard/workouts`,
    },
    {
      title: language === 'pt' ? "Sessões" : "Sessions",
      icon: ClipboardList,
      href: `${prefix}/student-dashboard/training-sessions`,
    },
    {
      title: language === 'pt' ? "Definições" : "Settings", 
      icon: Settings,
      href: `${prefix}/student-dashboard/settings`,
    }
  ];
  
  return [...baseItems, ...scheduleItem, ...restItems];
};

type StudentProfile = {
  id: string;
  email: string;
  has_schedule_access?: boolean;
  [key: string]: any;
}

export const useStudentNavItems = (language: 'en' | 'pt') => {
  const { user } = useAuth();
  
  const { data: studentProfile } = useQuery({
    queryKey: ["student-profile-nav", user?.id],
    queryFn: async () => {
      if (!user?.email) return null;
      try {
        const { data, error } = await supabase
          .from("students")
          .select("*")
          .eq("email", user.email)
          .eq("status", "active")
          .single();
          
        if (error) {
          console.error("Error fetching student profile for nav:", error);
          return null;
        }
        return data as StudentProfile;
      } catch (err) {
        console.error("Error in student profile query:", err);
        return null;
      }
    },
    enabled: !!user?.email
  });
  
  const hasScheduleAccess = studentProfile?.has_schedule_access !== false;
  
  return getStudentNavItems(language, hasScheduleAccess);
};

export const getTrainerNavItems = (language: 'en' | 'pt') => {
  return getDashboardNavItems(language);
};

export const studentNavItems = getStudentNavItems('en');
export const trainerNavItems = getDashboardNavItems('en');
